import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import ScrollToTop from "@/components/ScrollToTop";
import RouteMeta from "@/components/RouteMeta";
import ForgeAriaFab from "@/components/ForgeAriaFab";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Commesse from "@/pages/Commesse";
import Clienti from "@/pages/Clienti";
import Configurazione from "@/pages/Configurazione";
import Superadmin from "@/pages/Superadmin";
import MissionControl from "@/pages/MissionControl";
import SuperadminFounders from "@/pages/SuperadminFounders";
import SuperadminAriaInstances from "@/pages/SuperadminAriaInstances";
import SuperadminEcosystem from "@/pages/SuperadminEcosystem";
import TelegramBot from "@/pages/TelegramBot";
import InboxPage from "@/pages/Inbox";
import Landing from "@/pages/Landing";
import PublicLanding from "@/pages/PublicLanding";
import TrackOrder from "@/pages/TrackOrder";
import MarketingHome from "@/pages/MarketingHome";
import AppPresentation from "@/pages/AppPresentation";
import AriaWidget from "@/pages/AriaWidget";
import AriaInstanceConfig from "@/pages/AriaInstanceConfig";
import AriaEmbed from "@/pages/AriaEmbed";
import AriaDashboard from "@/pages/AriaDashboard";
import AriaCustomerArea from "@/pages/aria/AriaCustomerArea";
import AriaConsole from "@/pages/aria/AriaConsole";
import AriaAccountKnowledge from "@/pages/aria/AriaAccountKnowledge";
import AriaCreateWizard from "@/pages/aria/AriaCreateWizard";
import AriaInstall from "@/pages/aria/AriaInstall";
import AriaCustomize from "@/pages/aria/AriaCustomize";
import AriaKnowledge from "@/pages/aria/AriaKnowledge";
import AriaLead from "@/pages/aria/AriaLead";
import AriaPiano from "@/pages/aria/AriaPiano";
import AriaIsEverywhere from "@/pages/AriaIsEverywhere";
import WelcomeAria from "@/pages/WelcomeAria";
import SuperadminBilling from "@/pages/SuperadminBilling";
import Prodotti from "@/pages/Prodotti";
import AriaPortal from "@/pages/AriaPortal";
import LegalPage from "@/pages/LegalPage";
import LegalDPAAriaWidget from "@/pages/LegalDPAAriaWidget";
import LegalPrivacyTemplateWidget from "@/pages/LegalPrivacyTemplateWidget";
import AriaPromo from "@/pages/AriaPromo";
import AriaDemoPage from "@/pages/AriaDemoPage";
import AriaPiani from "@/pages/AriaPiani";
import BillingSuccess from "@/pages/BillingSuccess";
import ManualPage from "@/pages/ManualPage";
import AreaCliente from "@/pages/AreaCliente";
import TotalKlickADS from "@/pages/TotalKlickADS";
import TotalKlickADSDashboard from "@/pages/TotalKlickADSDashboard";
import TotalKlickADSBrand from "@/pages/TotalKlickADSBrand";
import TotalKlickADSCopyStudio from "@/pages/TotalKlickADSCopyStudio";
import TotalKlickADSLibrary from "@/pages/TotalKlickADSLibrary";
import TotalKlickADSCalendar from "@/pages/TotalKlickADSCalendar";
import TotalKlickADSSettings from "@/pages/TotalKlickADSSettings";
import TotalKlickHub from "@/pages/TotalKlickHub";
import Workspace from "@/pages/Workspace";
import Universe from "@/pages/universe/Universe";
import Memory from "@/pages/memory/Memory";
import ProvidersPage from "@/pages/providers/Providers";
import BenchPage from "@/pages/bench/Bench";
import ForgeAccessPage from "@/pages/superadmin/ForgeAccess";
import ForgeHome from "@/pages/forge/ForgeHome";
import AriaCockpit from "@/pages/forge/AriaCockpit";
import AriaFloatingWidget from "@/components/AriaFloatingWidget";

function Loading() {
    return (
        <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
            <div className="mono text-[12px] uppercase tracking-[0.15em]" style={{ color: "var(--muted)" }}>Caricamento TotalPL...</div>
        </div>
    );
}

function _redirectTargetFor(user) {
    if (user.role === "superadmin") return "/superadmin";
    // Honor ?next= when present (used by SSO and /app deep-links)
    if (typeof window !== "undefined") {
        const next = new URLSearchParams(window.location.search).get("next");
        if (next && next.startsWith("/")) return next;
    }
    return "/app";
}

function Protected({ children, role }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (user === false) return <Navigate to="/login" replace />;
    if (role && user.role !== role) return <Navigate to={_redirectTargetFor(user)} replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (user) return <Navigate to={_redirectTargetFor(user)} replace />;
    return children;
}

function HomeRedirect() {
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (user) return <Navigate to={_redirectTargetFor(user)} replace />;
    return <MarketingHome />;
}

function TotalPLHomeRedirect() {
    // /totalpl: utenti loggati → Dashboard TotalPL (o Superadmin panel); altrimenti marketing
    const { user, loading } = useAuth();
    if (loading || user === null) return <Loading />;
    if (user) {
        if (user.role === "superadmin") return <Navigate to="/superadmin" replace />;
        return <Navigate to="/dashboard" replace />;
    }
    return <MarketingHome />;
}

function Router() {
    const location = useLocation();
    const hideAriaWidget = location.pathname.startsWith("/promo")
        || location.pathname.startsWith("/aria/demo")
        || location.pathname === "/aria"
        || location.pathname.startsWith("/universe")
        || location.pathname.startsWith("/memory")
        || location.pathname.startsWith("/providers")
        || location.pathname.startsWith("/bench")
        || location.pathname.startsWith("/forge")
        || location.pathname.startsWith("/superadmin");
    return (
        <>
            <ScrollToTop />
            <RouteMeta />
            {!hideAriaWidget && <AriaFloatingWidget />}
            <ForgeAriaFab />
            <Routes>
            <Route path="/welcome" element={<WelcomeAria />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/t/:slug" element={<PublicLanding />} />
            <Route path="/track/:token" element={<TrackOrder />} />
            <Route path="/track" element={<TrackOrder />} />
            <Route path="/legal/dpa-aria-widget" element={<LegalDPAAriaWidget />} />
            <Route path="/legal/privacy-template-widget" element={<LegalPrivacyTemplateWidget />} />
            <Route path="/promo" element={<AriaPromo />} />
            <Route path="/legal/:kind" element={<LegalPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/manuale/:kind" element={<ManualPage />} />
            <Route path="/area-cliente" element={<AreaCliente />} />
            <Route path="/totalklickads" element={<TotalKlickADS />} />
            <Route path="/totalklickads/dashboard" element={<Protected><TotalKlickADSDashboard /></Protected>} />
            <Route path="/totalklickads/brand/:brandId" element={<Protected><TotalKlickADSBrand /></Protected>} />
            <Route path="/totalklickads/brand/:brandId/copy" element={<Protected><TotalKlickADSCopyStudio /></Protected>} />
            <Route path="/totalklickads/brand/:brandId/library" element={<Protected><TotalKlickADSLibrary /></Protected>} />
            <Route path="/totalklickads/brand/:brandId/calendar" element={<Protected><TotalKlickADSCalendar /></Protected>} />
            <Route path="/totalklickads/brand/:brandId/settings" element={<Protected><TotalKlickADSSettings /></Protected>} />
            <Route element={<Protected><AppLayout /></Protected>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/commesse" element={<Commesse />} />
                <Route path="/clienti" element={<Clienti />} />
                <Route path="/inbox" element={<InboxPage />} />
                <Route path="/telegram" element={<TelegramBot />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/configurazione" element={<Configurazione />} />
                <Route path="/superadmin/totalpl" element={<Superadmin />} />
            </Route>
            <Route path="/superadmin" element={<Protected role="superadmin"><MissionControl /></Protected>} />
            <Route path="/superadmin/founders" element={<Protected role="superadmin"><SuperadminFounders /></Protected>} />
            <Route path="/superadmin/aria" element={<Protected role="superadmin"><SuperadminAriaInstances /></Protected>} />
            <Route path="/superadmin/ecosystem" element={<Protected role="superadmin"><SuperadminEcosystem /></Protected>} />
            <Route path="/superadmin/billing" element={<Protected role="superadmin"><SuperadminBilling /></Protected>} />
            <Route path="/" element={<TotalKlickHub />} />
            <Route path="/universe" element={<Protected role="superadmin"><Universe /></Protected>} />
            <Route path="/memory" element={<Protected role="superadmin"><Memory /></Protected>} />
            <Route path="/providers" element={<Protected role="superadmin"><ProvidersPage /></Protected>} />
            <Route path="/bench" element={<Protected role="superadmin"><BenchPage /></Protected>} />
            <Route path="/superadmin/forge-access" element={<Protected role="superadmin"><ForgeAccessPage /></Protected>} />
            <Route path="/forge" element={<Protected role="superadmin"><ForgeHome /></Protected>} />
            <Route path="/forge/aria" element={<Protected role="superadmin"><AriaCockpit /></Protected>} />
            <Route path="/app" element={<Workspace />} />
            <Route path="/hub-classic" element={<Navigate to="/" replace />} />
            <Route path="/aria" element={<AriaPortal />} />
            <Route path="/aria/demo/:mode" element={<AriaDemoPage />} />
            <Route path="/aria/piani/:mode" element={<AriaPiani />} />
            <Route path="/aria/piani" element={<AriaPiani />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/aria-widget" element={<AriaWidget />} />
            <Route path="/aria/installa" element={<AriaWidget />} />
            <Route path="/aria/crea" element={<AriaCreateWizard />} />
            <Route path="/aria-experience" element={<AriaIsEverywhere />} />
            <Route path="/prodotti" element={<Prodotti />} />
            <Route path="/aria-widget/config/:token" element={<Protected><AriaInstanceConfig /></Protected>} />
            <Route path="/aria-embed" element={<AriaEmbed />} />
            <Route path="/app/aria" element={<Protected><AriaCustomerArea /></Protected>}>
                <Route index element={<AriaConsole />} />
                <Route path="console" element={<AriaConsole />} />
                <Route path="account" element={<AriaAccountKnowledge />} />
                <Route path="installazione" element={<AriaInstall />} />
                <Route path="personalizza" element={<AriaCustomize />} />
                <Route path="knowledge" element={<AriaKnowledge />} />
                <Route path="lead" element={<AriaLead />} />
                <Route path="piano" element={<AriaPiano />} />
            </Route>
            <Route path="/app/aria/legacy" element={<Protected><AriaDashboard /></Protected>} />
            <Route path="/totalpl" element={<TotalPLHomeRedirect />} />
            <Route path="/app/:app" element={<AppPresentation />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Router />
            </BrowserRouter>
        </AuthProvider>
    );
}
